import { createContext, PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { useRouteMatch } from 'react-router'

import { IApplicationState } from '@store'
import { ICampaign, selectCampaign } from '@store/campaigns'
import { IDraft, saveDraft, selectDraft, syncDraft } from '@store/drafts'
import { isEmpty } from '@utils'

export interface IDraftContextProps {
  draft: IDraft
  saveDraft: typeof saveDraft
}

export const DraftContext = createContext({ draft: {} } as IDraftContextProps)

// Base component for testing
export const DraftProvider: React.FC<
  PropsWithChildren<{
    campaignId?: number
    campaign?: ICampaign
    draft: IDraft
    saveDraft: typeof saveDraft
    syncDraft: typeof syncDraft
  }>
> = ({
  children,
  campaignId,
  campaign,
  draft,
  saveDraft: saveDraftAction,
  syncDraft: syncDraftAction,
}) => {
  const prevCampaignRef = useRef<ICampaign | undefined>(campaign)
  const calledRef = useRef(false)

  // Track whether syncDraft has been dispatched. For new campaigns (no campaignId) we are
  // always ready; for existing campaigns we must wait until syncDraft fires so that the
  // syncDraft saga (which is synchronous: select → put) can overwrite any stale preserved
  // draft before the builder subtree mounts and its effects run. Without this gate,
  // React's bottom-up useEffect order means child effects fire *before* the parent's
  // syncDraft effect, causing child effects to read a stale draft and race with the
  // subsequent syncDraft overwrite.
  const [syncDraftDispatched, setSyncDraftDispatched] = useState(!campaignId)

  // Call syncDraft once on mount if campaignId is present.
  useEffect(() => {
    if (campaignId && !calledRef.current) {
      syncDraftAction(campaignId)
      calledRef.current = true
      setSyncDraftDispatched(true)
    }
  }, [campaignId, syncDraftAction])

  useEffect(() => {
    const prevCampaign = prevCampaignRef.current
    if (campaignId && isEmpty(prevCampaign) && !isEmpty(campaign)) {
      syncDraftAction(campaignId)
    }
    prevCampaignRef.current = campaign
  }, [campaignId, campaign, syncDraftAction])

  // Children must not render until syncDraft has been dispatched (so the synchronous saga
  // can populate or refresh the draft store before child effects run) AND the draft is
  // non-empty. For new campaigns (no campaignId) both conditions are trivially true.
  const isReady = !campaignId || (syncDraftDispatched && !isEmpty(draft))

  const contextProps: IDraftContextProps = useMemo(
    () => ({
      draft,
      saveDraft: saveDraftAction,
    }),
    [draft, saveDraftAction],
  )

  return isReady ? (
    <DraftContext.Provider value={contextProps}>{children}</DraftContext.Provider>
  ) : null
}

// Connected component using hooks
const ConnectedDraftProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const match = useRouteMatch()
  const dispatch = useDispatch()

  const campaignIdParam = match.params && (match.params as any).campaignId
  const campaignId = campaignIdParam && parseInt(campaignIdParam, 10)
  const campaign = useSelector((state: IApplicationState) => selectCampaign(state, campaignIdParam))
  const draft = useSelector(selectDraft)

  return (
    <DraftProvider
      campaignId={campaignId}
      campaign={campaign}
      draft={draft}
      saveDraft={(d) => dispatch(saveDraft(d))}
      syncDraft={(cId) => dispatch(syncDraft(cId))}
    >
      {children}
    </DraftProvider>
  )
}

export default ConnectedDraftProvider
